import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FileText, Upload, Download, Trash2,
  Loader2, AlertTriangle, CheckCircle, ImageIcon, ZoomIn, ZoomOut, ExternalLink,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Accepted upload types — kept in sync with the backend
// /api/leads/{id}/upload-pef-pdf endpoint (Phase 18a.1).
const ACCEPT_ATTR = '.pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/jpeg,image/png,image/webp';
const ACCEPT_EXTS = ['.pdf', '.jpg', '.jpeg', '.png', '.webp'];
const MAX_BYTES = 10 * 1024 * 1024;

const guessMimeFromName = (name = '') => {
  const lower = name.toLowerCase();
  if (lower.endsWith('.pdf')) return 'application/pdf';
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
};

const isAccepted = (name = '') => {
  const lower = name.toLowerCase();
  return ACCEPT_EXTS.some(ext => lower.endsWith(ext));
};

// iter287 — Peek the first bytes of a Blob/File. A real PDF always starts
// with the ASCII magic string `%PDF`; everything else we treat as an image.
// This bypasses stored/declared MIME lies (e.g. PDFs mislabelled as
// image/jpeg during legacy uploads) that previously broke the viewer.
const detectKindFromBlob = async (blob) => {
  try {
    const slice = blob.slice(0, 5);
    const buf = await slice.arrayBuffer();
    const bytes = new Uint8Array(buf);
    if (
      bytes.length >= 4 &&
      bytes[0] === 0x25 && // %
      bytes[1] === 0x50 && // P
      bytes[2] === 0x44 && // D
      bytes[3] === 0x46    // F
    ) return 'pdf';
  } catch {
    /* ignore — fall through to image */
  }
  return 'image';
};

/**
 * PEF Document Card.
 *
 * Two operating modes:
 *
 *  1) **Persistent mode** (`leadId` provided): uploads / replaces / deletes
 *     against `/api/leads/{leadId}/upload-pef-pdf` and renders an inline
 *     preview pulled from `/api/leads/{leadId}/pef-pdf`.
 *
 *  2) **Staged mode** (`leadId` is null/empty, used during Add Account):
 *     no network call — the picked File is buffered locally and surfaced
 *     to the parent through `onStagedFileChange(file)`. The parent uploads
 *     it after the lead is created.
 *
 *  Renders a full visual preview:
 *   - images → `<img>` with zoom controls
 *   - PDFs   → embedded `<object>` viewer (browser-native, scrollable).
 */
const PefPdfCard = ({
  leadId,
  initialData = {},
  onStagedFileChange,
  className = '',
}) => {
  const { token } = useAuth();
  const [documentCollection, setDocumentCollection] = useState(initialData);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [stagedFile, setStagedFile] = useState(null);
  const [stagedPreviewUrl, setStagedPreviewUrl] = useState(null);
  const [stagedKind, setStagedKind] = useState(null); // iter287 — detected by magic bytes
  const [imgZoom, setImgZoom] = useState(1);
  const [persistedPreviewUrl, setPersistedPreviewUrl] = useState(null);
  const [persistedKind, setPersistedKind] = useState(null); // iter287 — detected by magic bytes
  const objectUrlsRef = useRef([]);

  // Refresh local state when initialData changes (e.g. parent fetches new lead).
  useEffect(() => {
    if (initialData) setDocumentCollection(initialData);
  }, [initialData]);

  // Stage-mode preview URL lifecycle.
  useEffect(() => {
    if (!stagedFile) { setStagedPreviewUrl(null); setStagedKind(null); return; }
    const url = URL.createObjectURL(stagedFile);
    setStagedPreviewUrl(url);
    objectUrlsRef.current.push(url);
    // iter287 — Detect real file type from magic bytes, not File.type or
    // extension (both of which lie in the field).
    let cancelled = false;
    detectKindFromBlob(stagedFile).then((k) => { if (!cancelled) setStagedKind(k); });
    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
      objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== url);
    };
  }, [stagedFile]);

  // Fetch persisted preview as a blob URL so we can render images / PDFs
  // inside the same DOM container without a separate auth dance.
  useEffect(() => {
    if (!leadId) { setPersistedPreviewUrl(null); setPersistedKind(null); return; }
    const hasFile = documentCollection?.pefPdfUrl || documentCollection?.pefPdfName;
    if (!hasFile) { setPersistedPreviewUrl(null); setPersistedKind(null); return; }
    let cancelled = false;
    let createdUrl = null;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/leads/${leadId}/pef-pdf`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const blob = await res.blob();
        if (cancelled) return;
        // iter287 — Detect real file type by magic bytes before deciding
        // whether to render as <object type="application/pdf"> or <img>.
        const kind = await detectKindFromBlob(blob);
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        objectUrlsRef.current.push(createdUrl);
        setPersistedPreviewUrl(createdUrl);
        setPersistedKind(kind);
      } catch {
        /* preview is best-effort; download still works */
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
        objectUrlsRef.current = objectUrlsRef.current.filter(u => u !== createdUrl);
      }
    };
  }, [leadId, documentCollection?.pefPdfUrl, documentCollection?.pefPdfName, documentCollection?.pefPdfUploadedAt, token]);

  // Final cleanup on unmount.
  useEffect(() => () => {
    objectUrlsRef.current.forEach(u => URL.revokeObjectURL(u));
    objectUrlsRef.current = [];
  }, []);

  const validateLocalFile = (file) => {
    if (!file) return 'No file selected';
    if (!isAccepted(file.name)) return 'Only PDF, JPG, JPEG, PNG, or WEBP files are allowed';
    if (file.size > MAX_BYTES) return 'File size exceeds 10MB limit';
    return null;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same filename later
    if (!file) return;
    const err = validateLocalFile(file);
    if (err) { setMessage({ type: 'error', text: err }); return; }

    // Staged mode — buffer locally; do NOT upload yet.
    if (!leadId) {
      setStagedFile(file);
      onStagedFileChange?.(file);
      setMessage({ type: 'success', text: `Selected: ${file.name}` });
      return;
    }

    // Persistent mode — POST immediately.
    try {
      setUploading(true);
      const fd = new FormData();
      fd.append('file', file);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/upload-pef-pdf`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!response.ok) {
        const errText = await response.text();
        let errMsg = 'Failed to upload document';
        try { errMsg = JSON.parse(errText)?.detail || errMsg; } catch { errMsg = errText || errMsg; }
        throw new Error(errMsg);
      }
      const data = await response.json();
      setDocumentCollection(prev => ({
        ...prev,
        pefPdfUrl: data.url,
        pefPdfName: data.filename,
        pefPdfMimeType: data.mime_type,
        pefPdfUploadedAt: new Date().toISOString(),
      }));
      setMessage({ type: 'success', text: 'PEF document uploaded.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setUploading(false);
    }
  };

  const clearStagedFile = () => {
    setStagedFile(null);
    onStagedFileChange?.(null);
  };

  const handleDeletePdf = async () => {
    if (!leadId) return;
    if (!window.confirm('Are you sure you want to delete this PEF document?')) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/pef-pdf`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to delete document');
      }
      setDocumentCollection(prev => ({
        ...prev, pefPdfUrl: null, pefPdfName: null, pefPdfMimeType: null,
      }));
      setPersistedPreviewUrl(null);
      setMessage({ type: 'success', text: 'PEF document deleted.' });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    if (!leadId) return;
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/api/leads/${leadId}/pef-pdf`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentCollection.pefPdfName || 'pef_document';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to download' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 4000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  // Active preview source/mime (staged > persisted).
  // iter287 — `kind` now comes from magic-byte detection (stagedKind /
  // persistedKind) rather than from the stored MIME type. While the magic
  // byte probe is still in flight we fall back to the extension/name
  // guess so the first render has something sensible.
  const previewSpec = useMemo(() => {
    if (stagedFile && stagedPreviewUrl) {
      const kind = stagedKind
        || (guessMimeFromName(stagedFile.name).startsWith('image/') ? 'image' : 'pdf');
      return {
        kind,
        url: stagedPreviewUrl,
        name: stagedFile.name,
        staged: true,
      };
    }
    if (persistedPreviewUrl) {
      const kind = persistedKind
        || (guessMimeFromName(documentCollection?.pefPdfName || '').startsWith('image/') ? 'image' : 'pdf');
      return {
        kind,
        url: persistedPreviewUrl,
        name: documentCollection?.pefPdfName || 'pef_document',
        staged: false,
      };
    }
    return null;
  }, [stagedFile, stagedPreviewUrl, stagedKind, persistedPreviewUrl, persistedKind, documentCollection?.pefPdfName]);

  const hasPersisted = !!leadId && (documentCollection?.pefPdfUrl || documentCollection?.pefPdfName);

  return (
    <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`} data-testid="pef-pdf-card">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-blue-600" />
          <h3 className="font-semibold text-gray-800">PEF Document</h3>
        </div>
        {previewSpec?.kind === 'image' && (
          <div className="flex items-center gap-1" data-testid="pef-zoom-controls">
            <button
              type="button"
              onClick={() => setImgZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Zoom out"
              data-testid="pef-zoom-out"
            >
              <ZoomOut size={16} />
            </button>
            <span className="text-xs text-gray-500 w-10 text-center tabular-nums">{Math.round(imgZoom * 100)}%</span>
            <button
              type="button"
              onClick={() => setImgZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))}
              className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Zoom in"
              data-testid="pef-zoom-in"
            >
              <ZoomIn size={16} />
            </button>
          </div>
        )}
      </div>

      {message.text && (
        <div className={`mx-4 mt-4 flex items-center gap-2 px-3 py-2 rounded-lg text-sm ${
          message.type === 'error' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'
        }`} data-testid="pef-message">
          {message.type === 'error' ? <AlertTriangle size={16} /> : <CheckCircle size={16} />}
          {message.text}
        </div>
      )}

      <div className="p-4 space-y-3">
        {previewSpec ? (
          <>
            <div className="flex items-center justify-between gap-3" data-testid="pef-preview-toolbar">
              <div className="flex items-center gap-2 min-w-0">
                {previewSpec.kind === 'image'
                  ? <ImageIcon size={16} className="text-blue-600 shrink-0" />
                  : <FileText size={16} className="text-blue-600 shrink-0" />}
                <p className="text-sm font-medium text-gray-700 truncate" title={previewSpec.name}>{previewSpec.name}</p>
                {previewSpec.staged && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded" data-testid="pef-staged-badge">
                    Pending upload
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {/* iter287 — Always-visible Open-in-new-tab fallback so a
                    user can escape the embedded viewer if the browser
                    can't render the blob inline (e.g. mobile Safari). */}
                <a
                  href={previewSpec.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                  title="Open in new tab"
                  data-testid="pef-open-newtab-btn"
                >
                  <ExternalLink size={16} />
                </a>
                <label className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg cursor-pointer" title="Replace document">
                  <input
                    type="file"
                    accept={ACCEPT_ATTR}
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                    data-testid="pef-replace-input"
                  />
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                </label>
                {hasPersisted && (
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg"
                    title="Download"
                    data-testid="download-pef-pdf-btn"
                  >
                    <Download size={16} />
                  </button>
                )}
                {previewSpec.staged ? (
                  <button
                    type="button"
                    onClick={clearStagedFile}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Remove staged file"
                    data-testid="pef-remove-staged-btn"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : hasPersisted && (
                  <button
                    type="button"
                    onClick={handleDeletePdf}
                    disabled={loading}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    title="Delete"
                    data-testid="delete-pef-pdf-btn"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                  </button>
                )}
              </div>
            </div>

            {previewSpec.kind === 'image' ? (
              <div className="bg-gray-50 border border-gray-200 rounded-lg max-h-[70vh] overflow-auto" data-testid="pef-image-preview">
                <img
                  src={previewSpec.url}
                  alt={previewSpec.name}
                  style={{ transform: `scale(${imgZoom})`, transformOrigin: 'top left' }}
                  className="block max-w-none transition-transform"
                />
              </div>
            ) : (
              // iter287 — Blob URL passed WITHOUT `#toolbar=1&navpanes=0` so
              // Chromium's new PDF viewer doesn't choke on the fragment.
              <object
                data={previewSpec.url}
                type="application/pdf"
                className="w-full h-[70vh] rounded-lg border border-gray-200 bg-gray-100"
                data-testid="pef-pdf-embed"
              >
                <p className="p-4 text-sm text-gray-600">
                  PDF preview is not available in this browser. <a className="text-teal-600 underline" href={previewSpec.url} target="_blank" rel="noopener noreferrer" data-testid="pef-open-newtab-fallback">Open in new tab</a>.
                </p>
              </object>
            )}
          </>
        ) : (
          <label className="flex flex-col items-center justify-center gap-2 p-8 border-2 border-dashed border-gray-200 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/40 transition-colors" data-testid="pef-empty-uploader">
            <input
              type="file"
              accept={ACCEPT_ATTR}
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
              data-testid="upload-pef-pdf-input"
            />
            {uploading
              ? <Loader2 size={24} className="animate-spin text-blue-600" />
              : <Upload size={24} className="text-gray-400" />}
            <span className="text-sm font-medium text-gray-600">
              {uploading ? 'Uploading…' : 'Upload PEF Document'}
            </span>
            <span className="text-xs text-gray-400">PDF, JPG, PNG, or WEBP — max 10 MB</span>
          </label>
        )}
      </div>
    </div>
  );
};

export default PefPdfCard;
